<template>
  <div class="message-area" v-if="zanFurikyuu && zanFurikyuu.zan > 0">
    <div class="message info">
      <p>
        振休残が{{ zanFurikyuu.zan }}日あります
      </p>
    </div>
  </div>
  <!-- 半日休取得可 -->
  <!-- 雇用契約の勤務時間が７時間以上が対象 -->
  <div class="multi-date-table" v-if="form.yuukyuu" v-for="(item, index) in form.yuukyuu" :key="index">
    <div class="head"><b>有給{{ (index + 1) }}</b></div>
    <div class="item">
      <b class="required">取得日</b>
      <date-picker v-model="item.start_date" :error="getError(item, 'start_date')"></date-picker>
    </div>

    <div class="item">
      <koyouKeiyakuTimeSelect v-if="isInit" :errors="form.errors" :targetDate="item.start_date"
        :koyouKeiyakuId="koyouKeiyakuId" :workTimeId="workTimeId" @change="changeKoyouKeiyakuTime">
      </koyouKeiyakuTimeSelect>
    </div>

    <!--<p class="danger-text">shinsei_koyou_keiyaku_time[index]:<br>{{ form.shinsei_koyou_keiyaku_time[index] }}</p>-->

    <div class="item" v-if="form.shinsei_koyou_keiyaku_time[index] && selectedSyuugyouBasyoWithKoyouKeiyakuOption">
      <b class="required">区分（取得時間）</b>
      <div class="yuukyuu-kubun">
        <select-box :options="getYuukyuuTaniOption(index)" v-model="item.yuukyuu_tani_type"
          :error="getError(item, 'yuukyuu_tani_type')" :hasBlank="false"></select-box>
        <p>（{{ getYuukyuuTime(
          item.yuukyuu_tani_type,
          selectedSyuugyouBasyoWithKoyouKeiyakuOption.sashihiki_times,
          item,
          form.shinsei_koyou_keiyaku_time[index]
        ) }}時間）</p>
      </div>
    </div>
    <div class="row-button" v-if="(form.yuukyuu.length > 1)">
      <i class="fa-solid fa-delete-left" @click="removeTableRow(form.yuukyuu, index)"></i>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, toRefs, ref, onMounted, nextTick, Ref, watch } from "vue";
import { useForm } from "@/composable/form/form";
import { useTableRow } from "@/composable/common/tablerow";
import OptionConst from '@/const/Option';
import { Shinsei } from '@/types/Shinsei';
import dayjs from "dayjs";
import { KoyouKeiyakuTimeSelectEmitData, SyuugyouBasyoAndKoyouKeiyakuOption } from "@/types/SyuugyouBasyo";
import KoyouKeiyakuTImeSelect from "@/components/shinsei/create/parts/KoyouKeiyakuTimeSelect.vue"
import { KoyouKeiyakuTimeAndHayabanOsobanOption, KoyouKeiyakuTimeAndHayabanOsobanOptions } from "@/types/KoyouKeiyakuTime";
import { useYuukyuu } from "@/composable/shinsei/yuukyuu/yuukyuu";
import { useFurikyuu } from "@/composable/shinsei/furikyuu/furikyuu";
import { DateUtil } from "@/utils/DateUtil";
import { useSyuugyouBasyoInit } from "@/composable/syuubyoubasyo/init";
import { Logger } from "@/utils/Logger";
import { useAuthStore } from "@/store/auth";

export default defineComponent({
  props: {
    form: {
      type: Object as () => Shinsei,
      default: null
    },
    koyouKeiyakuId: {
      // 編集時は申請情報を持つ親コンポーネントから就業時間IDを受け取る
      type: Number as () => number | null,
      default: null
    },
    workTimeId: {
      // 編集時は申請情報を持つ親コンポーネントから就業時間IDを受け取る
      type: Number as () => number | null,
      default: null
    },
  },
  emits: {
    changeKoyouKeiyakuTime: (
      targetDate: string | Date | null,
      selectedSyuugyouBasyo: SyuugyouBasyoAndKoyouKeiyakuOption | null,
      selectedKoyouKeiyakuTimeOption: KoyouKeiyakuTimeAndHayabanOsobanOption | null,
      koyouKeiyakuTimeAndHayabanOsobanOptions: KoyouKeiyakuTimeAndHayabanOsobanOptions
    ) => true,
  },
  setup(props, context) {
    const { form, koyouKeiyakuId, workTimeId, } = toRefs(props);
    const { selectedSyuugyouBasyoWithKoyouKeiyakuOption } = useSyuugyouBasyoInit()
    const { removeTableRow } = useTableRow();
    const { getYuukyuuTime } = useYuukyuu();
    const { zanFurikyuu, getZanFurikyuuCount } = useFurikyuu();

    // エラー取得
    const { getError } = useForm();

    // 選択中の就業場所／就業時間等（MEDICA_AS-959対応）
    const selectedWorkTimeOption: Ref<KoyouKeiyakuTimeAndHayabanOsobanOption | null> = ref(null)
    const hayabanOsobanOpt: Ref<KoyouKeiyakuTimeAndHayabanOsobanOption[]> = ref([])
    const sashihikiTimes: Ref<string | null> = ref(null)
    const date: Ref<string | Date | null> = ref(null)
    // もともと、ある就業場所を選んでそれから日付を選んでの申請だったが、959では日付を選んでからその日付で選択可能な就業場所を選ぶようにしている。
    // formの構造も前者の仕様に沿った実装になっているが、959の仕様に合わせるにはform.yuukyuu[]の各要素に就業場所等をもたせる必要がある。
    // しかしその構造を変えるとサーバー側の実装も変える必要があり作業負荷が重いことや、１申請で１日しか取れない運用なのでひとまずこのこうした持たせ方にしている

    const isInit = ref(false)

    onMounted(async () => {
      Logger.debug('----------------------------\n有休申請コンポーネント（子）の初期化開始')
      Logger.debug('有休申請（子） onMounted', { '申請': form.value })

      // 画面を開いたタイミングで振休残数を取得する
      // 振休残数は「入社後の全雇用契約を通じた累計」で運用するため、雇用契約IDは渡さない
      // （ShinseiService.php getZanFurikyuuCount のコメント参照）
      const authStore = useAuthStore()
      const userId = form.value.user_id ?? authStore.user?.user_id
      if (userId) {
        await getZanFurikyuuCount(userId)
      }

      nextTick(() => {
        isInit.value = true
        Logger.debug('時間外申請コンポーネントの初期化完了\n----------------------------')
      })
    })

    // 就業場所・就業時間が変更された場合に走る処理
    // 引数は以下コンポーネントのemits.changeに準拠
    // src/resources/ts/components/shinsei/create/parts/KoyouKeiyakuTimeSelect.vue
    const changeKoyouKeiyakuTime = (emitData: KoyouKeiyakuTimeSelectEmitData) => {
      Logger.debug('申請雇用契約時間をもとに申請雇用契約時間のセットをします')
      if (!form.value.yuukyuu) return // 親コンポーネントにて必ずform.value.yuukyuuは初期化しているので形チェックエラー回避のため

      // このコンポーネント内で使用するためコンポーネント内の変数に保持
      selectedSyuugyouBasyoWithKoyouKeiyakuOption.value = emitData.selectedSyuugyouBasyo
      selectedWorkTimeOption.value = emitData.selectedKoyouKeiyakuTimeOption
      sashihikiTimes.value = emitData.koyouKeiyakuTimeAndHayabanOsobanOptions.sashihikiTime
      hayabanOsobanOpt.value = emitData.koyouKeiyakuTimeAndHayabanOsobanOptions.hayabanOsobanOpt
      date.value = emitData.targetDate

      // 就業場所に関する情報をemitする（申請オブジェクトの変更等は親に移譲する）
      context.emit('changeKoyouKeiyakuTime', emitData.targetDate, emitData.selectedSyuugyouBasyo, emitData.selectedKoyouKeiyakuTimeOption, emitData.koyouKeiyakuTimeAndHayabanOsobanOptions);
    }

    const getKoyouKeiyakuTimeId = (date: string | Date | null) => {
      const d = dayjs(date)
      return form.value.shinsei_koyou_keiyaku_time?.find(function (v, k) {
        return d.isSame(v.target_date)
      })?.koyou_keiyaku_time_id
    }

    // 有休申請の1日、AM休、PM休のセレクトボックス値を取得
    const getYuukyuuTaniOption = (index: number) => {
      Logger.debug('getYuukyuuTaniOption')
      // 雇用契約の有休差引時間が設定されている場合
      if (sashihikiTimes.value !== "00:00:00") {
        // 1日申請のみ可能
        return OptionConst.SHINSEI_DIV_OPTION.filter(
          (v) => v.val === OptionConst.SHINSEI_DIV.FULL_DAY
        )
      }

      // 就業時間未選択時
      if (!form.value.shinsei_koyou_keiyaku_time[index]) {
        // 有休申請の1日、AM休、PM休を選択可能とする
        return OptionConst.SHINSEI_DIV_OPTION
      }

      // 半日申請可能な場合
      if (form.value.shinsei_koyou_keiyaku_time[index].isHannichi) {
        // 有休申請の1日、AM休、PM休を選択可能とする
        return OptionConst.SHINSEI_DIV_OPTION

      } else {
        // console.log("form.value.shinsei_koyou_keiyaku_time[index]")
        // console.log(form.value.shinsei_koyou_keiyaku_time[index])

        // AM有給しか取得できないかどうかのフラグを取得できる場合
        if (form.value.shinsei_koyou_keiyaku_time[index].canOnlyAmKinmu) {
          return OptionConst.SHINSEI_DIV_OPTION.filter(
            (v) => v.val === OptionConst.SHINSEI_DIV.AM
          )
        }

        // PM有給しか取得できないかどうかのフラグを取得できる場合
        if (form.value.shinsei_koyou_keiyaku_time[index].canOnlyPmKinmu) {
          return OptionConst.SHINSEI_DIV_OPTION.filter(
            (v) => v.val === OptionConst.SHINSEI_DIV.PM
          )
        }

        // 対象日に早番遅番申請が出ている場合、一致する早番遅番の情報を取得
        const matchedayabanOsobanOption = hayabanOsobanOpt.value.filter((v: any) => v.date === DateUtil.format(date.value?.toString(), 'YYYY/MM/DD'))

        // console.log('選択日の早番遅番');
        // console.log(hayabanOsobanOption);

        if (matchedayabanOsobanOption.length > 0) {
          // 早番遅番申請が提出されている場合、全ての取得区分（1日、AM休、PM休）を選択可能とする
          return OptionConst.SHINSEI_DIV_OPTION
        }


        // 1日申請のみ可能な場合
        return OptionConst.SHINSEI_DIV_OPTION.filter(
          (v) => v.val === OptionConst.SHINSEI_DIV.FULL_DAY
        )
      }
    }

    return {
      getError,
      removeTableRow,
      getYuukyuuTime,
      'kyuukaDiv': OptionConst.SHINSEI_DIV_OPTION,
      getKoyouKeiyakuTimeId,
      getYuukyuuTaniOption,
      isInit,
      changeKoyouKeiyakuTime,
      selectedSyuugyouBasyoWithKoyouKeiyakuOption,
      koyouKeiyakuId,
      workTimeId,
      zanFurikyuu
    };
  },
  components: {
    'koyouKeiyakuTimeSelect': KoyouKeiyakuTImeSelect
  }
});
</script>

<style lang="scss" scoped>
@import '@scss/components/shinsei-create.scss';
</style>
